import React, { useContext, useState, useMemo } from 'react';
import { ThemeContext } from '../hooks/ThemeContext';
import { usePortfolioProducts } from '../hooks/useProducts';
import Watermark from '../components/common/Watermark';
import ProtectedImage from '../components/common/ProtectedImage';
import LoadingSpinner from '../components/globals/LoadingSpinner';

const Creations = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const { products, loading, error } = usePortfolioProducts();
  const [active, setActive] = useState('all');

  const normalizedProducts = useMemo(() => (
    products.map((p) => ({
      id: p.id,
      type: (p.category || 'autre').toLowerCase(),
      title: p.name || p.title || 'Création',
      client: p.creator_name || 'Krayne Agency',
      src: p.image || null,
    }))
  ), [products]);

  const tabs = useMemo(() => {
    const categoryKeys = Array.from(new Set(normalizedProducts.map(p => p.type)));
    const dynamicTabs = categoryKeys.map(key => ({ key, label: key.charAt(0).toUpperCase() + key.slice(1) }));
    return [
      { key: 'all', label: 'Tout' },
      ...dynamicTabs,
    ];
  }, [normalizedProducts]);

  const filtered = useMemo(() => (
    active === 'all' ? normalizedProducts : normalizedProducts.filter(i => i.type === active)
  ), [active, normalizedProducts]);

  const cardBase = isDark
    ? 'bg-[#0b0b0c] border border-white/6'
    : 'bg-white/95 border border-gray-100';

  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <header className="mb-8">
          <h1 className="text-3xl font-bold">Portfolio - Nos Créations</h1>
          <p className="text-lg opacity-90">
            Galerie de nos réalisations : logos, bannières, produits print et contenus sociaux. 
            <br />
            <span className={isDark ? 'text-rose-400' : 'text-rose-600'}>
              Pour acheter ces créations, rendez-vous sur notre <a href="/shop" className="underline hover:opacity-80">Boutique</a>
            </span>
          </p>
        </header>

        {error && (
          <div className="mb-6 text-red-500">{error}</div>
        )}

        {loading ? (
          <div className="flex justify-center py-10">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {/* Filtres */}
            {tabs.length > 1 && (
              <nav className="flex flex-wrap gap-2 mb-6" aria-label="Filtres créations">
                {tabs.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setActive(t.key)}
                    className={`px-4 py-2 rounded-md text-sm transition border select-none ${active === t.key
                      ? (isDark ? 'bg-rose-600 text-white border-rose-600' : 'bg-rose-600 text-white border-rose-600')
                      : (isDark ? 'bg-[#0b0b0c] text-white border-white/10 hover:border-rose-500/40' : 'bg-white/95 text-gray-800 border-gray-200 hover:border-rose-300')}`}
                  >
                    {t.label}
                  </button>
                ))}
              </nav>
            )}

            {/* Grille */}
            {filtered.length === 0 ? (
              <div className="text-center py-12 text-sm opacity-80">
                Aucune création disponible pour le moment.
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {filtered.map((p) => (
                  <article key={p.id} className={`rounded-xl overflow-hidden shadow-sm ${cardBase} transition hover:shadow-lg`}>
                    {/* Zone image avec filigrane et protection */}
                    <Watermark className={`h-36 md:h-40 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                      {p.src ? (
                        <ProtectedImage
                          imagePath={p.src}
                          alt={p.title}
                          className="w-full h-full"
                          objectFit="cover"
                          showWatermark={true}
                        />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span>Prévoir visuel ici</span>
                        </div>
                      )}
                    </Watermark>

                    <div className="p-4">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="font-semibold text-sm md:text-base">{p.title}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs ${isDark ? 'bg-rose-600/20 text-rose-300' : 'bg-rose-50 text-rose-600'}`}>{p.type}</span>
                      </div>
                      <p className={`text-xs md:text-sm mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{p.client}</p>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}

        {/* CTA */}
        <div className="mt-10 text-center space-y-4">
          <a href="/shop" className={`inline-block px-6 py-3 rounded-md font-semibold ${isDark ? 'bg-rose-500 text-white hover:bg-rose-600' : 'bg-rose-600 text-white hover:bg-rose-700'} mr-4`}>
            Acheter des créations
          </a>
          <a href="/contact" className={`inline-block px-6 py-3 rounded-md font-semibold border ${isDark ? 'border-white/20 text-white hover:bg-white/5' : 'border-gray-300 text-gray-800 hover:bg-gray-50'}`}>
            Demander un portfolio complet
          </a>
        </div>
      </div>
    </div>
  );
};

export default Creations;
